import { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import uPlot, { type AlignedData, type Options } from "uplot";
import { useResolvedTheme } from "@/lib/theme";
import { formatTime } from "@/lib/format";

/**
 * Missing seconds up to this long are drawn as a continuous line. Samples
 * are bucketed per second while each node reports on its own ~1 s clock,
 * so a sample now and then lands in the same second as the previous one
 * and leaves the next second empty; that is jitter, not missing traffic.
 * A longer hole (a node restarting, a lost stream) stays a visible gap.
 * The tooltip still shows "—" for a second with no sample.
 */
export const MAX_BRIDGED_GAP_SEC = 5;

/**
 * A uPlot series.gaps filter that keeps only gaps whose data hole spans
 * more than maxSec seconds. uPlot passes each gap as [left px, right px]
 * between the last value before it and the first value after it.
 */
export function keepLongGaps(maxSec: number) {
  return (u: uPlot, _sidx: number, _i0: number, _i1: number, gaps: [number, number][]): [number, number][] =>
    gaps.filter(([l, r]) => u.posToVal(r, "x") - u.posToVal(l, "x") > maxSec + 1);
}

export interface ChartSeries {
  key: string;
  label: string;
  /** A CSS custom property holding the color, e.g. "--series-1". */
  colorVar: string;
  values: (number | null)[];
}

export interface Band {
  start: number; // unix seconds
  end: number;
}

export interface TimeSeriesChartProps {
  /** Unix seconds, ascending. */
  xs: number[];
  series: ChartSeries[];
  formatValue: (v: number) => string;
  /** Short axis tick formatter (defaults to formatValue). */
  formatTick?: (v: number) => string;
  height?: number;
  /** Shaded windows (active/recent attacks), drawn behind the lines. */
  bands?: Band[];
  /** Fill under a single series with a 10% wash. */
  area?: boolean;
  ariaLabel: string;
}

function cssVar(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || "#888";
}

function withAlpha(hex: string, alpha: number): string {
  const m = hex.match(/^#([0-9a-f]{6})$/i);
  if (!m) return hex;
  const n = parseInt(m[1]!, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

/**
 * Tooltip plugin: one readout listing every series at the crosshair's X,
 * values first (strong), labels second, keyed by a short line of the series
 * color. Built with textContent only — series labels come from the API.
 */
function tooltipPlugin(
  seriesRef: { current: ChartSeries[] },
  formatValue: (v: number) => string,
  colors: string[],
): uPlot.Plugin {
  let tip: HTMLDivElement;
  return {
    hooks: {
      init: (u) => {
        tip = document.createElement("div");
        tip.className =
          "pointer-events-none absolute z-10 hidden min-w-40 rounded-md border border-line-strong bg-surface px-2.5 py-2 text-xs shadow-lg";
        u.over.appendChild(tip);
        u.over.addEventListener("mouseleave", () => tip.classList.add("hidden"));
      },
      setCursor: (u) => {
        const idx = u.cursor.idx;
        const left = u.cursor.left ?? -1;
        if (idx == null || left < 0) {
          tip.classList.add("hidden");
          return;
        }
        tip.replaceChildren();
        const head = document.createElement("div");
        head.className = "mb-1 text-ink-3 tabular";
        head.textContent = formatTime((u.data[0][idx] as number) * 1000);
        tip.appendChild(head);
        seriesRef.current.forEach((s, i) => {
          const v = u.data[i + 1]?.[idx];
          const row = document.createElement("div");
          row.className = "flex items-center gap-2 py-0.5";
          const key = document.createElement("span");
          key.className = "inline-block h-0.5 w-3 shrink-0 rounded";
          key.style.background = colors[i] ?? "#888";
          const val = document.createElement("span");
          val.className = "font-semibold text-ink tabular";
          val.textContent = v == null ? "—" : formatValue(v);
          const label = document.createElement("span");
          label.className = "ml-auto pl-3 text-ink-2";
          label.textContent = s.label;
          row.append(key, val, label);
          tip.appendChild(row);
        });
        tip.classList.remove("hidden");
        const w = tip.offsetWidth;
        const overW = u.over.clientWidth;
        const x = left + 14 + w > overW ? left - 14 - w : left + 14;
        tip.style.left = `${Math.max(0, x)}px`;
        tip.style.top = "8px";
      },
    },
  };
}

function bandsPlugin(bandsRef: { current: Band[] }, fill: string): uPlot.Plugin {
  return {
    hooks: {
      drawClear: (u) => {
        const ctx = u.ctx;
        ctx.save();
        ctx.fillStyle = fill;
        for (const b of bandsRef.current) {
          const x0 = u.valToPos(b.start, "x", true);
          const x1 = u.valToPos(b.end, "x", true);
          const left = Math.max(u.bbox.left, Math.min(x0, x1));
          const right = Math.min(u.bbox.left + u.bbox.width, Math.max(x0, x1));
          if (right > left) ctx.fillRect(left, u.bbox.top, Math.max(right - left, 2), u.bbox.height);
        }
        ctx.restore();
      },
    },
  };
}

export function TimeSeriesChart({
  xs,
  series,
  formatValue,
  formatTick,
  height = 240,
  bands = [],
  area = false,
  ariaLabel,
}: TimeSeriesChartProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const plotRef = useRef<uPlot | null>(null);
  const seriesRef = useRef(series);
  const bandsRef = useRef(bands);
  const theme = useResolvedTheme();

  // Rebuild only when the series *set* or theme changes; data updates go
  // through setData so the chart never flashes on a new sample.
  const shape = series.map((s) => `${s.key}:${s.colorVar}:${s.label}`).join("|");

  const data = useMemo<AlignedData>(() => [xs, ...series.map((s) => s.values)] as AlignedData, [xs, series]);
  const dataRef = useRef(data);

  // Keep the refs the uPlot hooks read in sync; layout effects run before
  // the effects below, so a (re)built chart always sees current values.
  useLayoutEffect(() => {
    seriesRef.current = series;
    bandsRef.current = bands;
    dataRef.current = data;
  });

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const colors = seriesRef.current.map((s) => cssVar(s.colorVar));
    const ink3 = cssVar("--ink-3");
    const grid = cssVar("--grid");
    const axis = cssVar("--axis");
    const tick = formatTick ?? formatValue;
    const font = '11px "Inter Variable", system-ui, sans-serif';

    const opts: Options = {
      width: host.clientWidth || 600,
      height,
      padding: [12, 8, 0, 0],
      legend: { show: false },
      cursor: {
        y: false,
        points: { size: 8, width: 2, stroke: cssVar("--surface") },
        drag: { x: false, y: false },
      },
      scales: { x: { time: true }, y: { range: (_u, _min, max) => [0, max > 0 ? max * 1.1 : 1] } },
      axes: [
        {
          stroke: ink3,
          font,
          grid: { show: false },
          ticks: { stroke: axis, width: 1, size: 4 },
          values: (_u, vals) => vals.map((v) => formatTime(v * 1000)),
          space: 70,
        },
        {
          stroke: ink3,
          font,
          size: 64,
          grid: { stroke: grid, width: 1 },
          ticks: { show: false },
          values: (_u, vals) => vals.map((v) => tick(v)),
        },
      ],
      series: [
        {},
        ...seriesRef.current.map((s, i) => ({
          label: s.label,
          stroke: colors[i],
          width: 2,
          fill: area && seriesRef.current.length === 1 ? withAlpha(colors[i]!, 0.1) : undefined,
          points: { show: false },
          spanGaps: false,
          gaps: keepLongGaps(MAX_BRIDGED_GAP_SEC),
        })),
      ],
      plugins: [tooltipPlugin(seriesRef, formatValue, colors), bandsPlugin(bandsRef, withAlpha(cssVar("--critical"), 0.12))],
    };
    const u = new uPlot(opts, dataRef.current, host);
    plotRef.current = u;

    const ro = new ResizeObserver(() => {
      if (host.clientWidth > 0) u.setSize({ width: host.clientWidth, height });
    });
    ro.observe(host);
    return () => {
      ro.disconnect();
      u.destroy();
      plotRef.current = null;
    };
    // formatValue/formatTick are stable module functions in practice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shape, theme, height, area]);

  useEffect(() => {
    plotRef.current?.setData(data);
  }, [data]);

  return <div ref={hostRef} role="img" aria-label={ariaLabel} className="relative w-full" style={{ height }} />;
}

export function Legend({ items }: { items: { key: string; label: string; colorVar: string; value?: string }[] }) {
  return (
    <ul className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-2">
      {items.map((it) => (
        <li key={it.key} className="flex items-center gap-1.5">
          <span className="inline-block h-0.5 w-3.5 rounded" style={{ background: `var(${it.colorVar})` }} aria-hidden />
          <span>{it.label}</span>
          {it.value !== undefined && <span className="font-semibold text-ink tabular">{it.value}</span>}
        </li>
      ))}
    </ul>
  );
}

export function Sparkline({ values, width = 96, height = 24 }: { values: number[]; width?: number; height?: number }) {
  if (values.length < 2) return <svg width={width} height={height} aria-hidden />;
  const max = Math.max(...values, 1);
  const step = width / (values.length - 1);
  const pts = values.map((v, i) => `${(i * step).toFixed(1)},${(height - 2 - (v / max) * (height - 4)).toFixed(1)}`);
  const last = pts[pts.length - 1]!.split(",");
  return (
    <svg width={width} height={height} aria-hidden className="overflow-visible">
      <polyline points={pts.join(" ")} fill="none" stroke="var(--ink-3)" strokeWidth={1.5} strokeLinejoin="round" />
      <circle cx={last[0]} cy={last[1]} r={2.5} fill="var(--accent)" />
    </svg>
  );
}
